import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { FranklinMadeMark } from "@/domains/documents/franklinMade/FranklinMadeMark";
import { useFranklinDisplayName } from "@/domains/documents/franklinMade/franklinMade";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { DocumentStateBadge } from "@/components/ui/documents/document-state-badge";
import { VerifiedBadge } from "@/components/ui/documents/verified-badge";
import { HIGHLIGHTED_ROW_CLASS, useDocumentRowDecorations } from "./documentRowDecorations";
import { RowChipBadge } from "./RowChipBadge";
import { CommentCountBadge } from "../comments/CommentCountBadge";
import type { DocumentCommentCounts } from "@/stores/documents/comments/types";
import { DocumentCheckbox } from "@/domains/documents/components/molecules/DocumentCheckbox";
import { DocumentActions } from "@/domains/documents/components/molecules/DocumentActions";
import { DocumentNameInlineEdit } from "@/domains/documents/components/atoms/DocumentNameInlineEdit";
import type { TagReference } from "@/domains/documents/components/molecules/DocumentTags";
import { DocumentTagEditorButton } from "../atoms/DocumentTagEditorButton";
import { DocumentTags } from "@/domains/documents/components/molecules/DocumentTags";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import {
  ChevronRight,
  Download,
  Eye,
  FileText,
  FolderOpen,
  Plus,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import type { FolderPathSegment } from "../../utils/folderPathResolver";
import { BreadcrumbTooltipContent } from "../atoms/BreadcrumbTooltipContent";
import { cn } from "@/lib/utils";
import type { DocumentState } from "@/types/documents";
import { isDocumentProcessing } from "@/domains/documents/utils/documentState";
import {
  isContainerFile,
  isSignedContainerFile,
  isSupportedFiletype,
} from "@/domains/documents/constants/fileTypes";
import {
  InContainerChip,
  SignedFileIcon,
} from "@/domains/documents/components/atoms/SignedContainerIndicators";
import { DocumentDateCell } from "@/domains/documents/components/atoms/DocumentDateCell";
import { formatFileSize } from "@/utils/formatFileSize";
import { DocumentUploaderCell } from "@/domains/documents/components/atoms/DocumentUploaderCell";
import { documentGridTemplate } from "@/domains/documents/utils/fitDocumentColumns";
import { EnclosedDocumentsMenu } from "@/domains/documents/components/molecules/EnclosedDocumentsMenu";
import { useSignedContainerInfo } from "@/domains/documents/signing/useSignedContainerInfo";
import { useSignaturePanel } from "@/domains/documents/signing/signaturePanelContext";
import { canInspectSignatures } from "@/domains/documents/signing/signatureInspection";
import { canPreviewDocumentRow } from "@/utils/previewUtils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { ContentMatchInfo } from "@/stores/documents/repository";
import { formatContentMatchTooltip } from "../../utils/contentMatchTooltip";
import { hasVersionHistory } from "../../utils/versionHistory";
import {
  LinkedIssuesChip,
  type LinkedIssueRef,
} from "@/domains/pm/components/issues/LinkedIssuesChip";

interface DocumentTableRowProps {
  /** Document data */
  document: {
    id: string;
    filename: string;
    content_type: string;
    size: number;
    created_at: string;
    original_created_at?: string | null;
    modified_at?: string | null;
    document_content_modified_date?: string | null;
    created_by?: string | number | null;
    created_by_name: string | null;
    created_by_avatar_url?: string | null;
    created_by_color?: string | null;
    state: DocumentState | null;
    version: number | null;
    version_group: string | null;
    parent_document_id: string | null;
    /** Verified mark; `verified_at` being non-null IS the boolean. */
    verified_at?: string | null;
    verified_by_name?: string | null;
  };
  /** Parent document filename (if exists) */
  parentDocumentName?: string;
  /** Current selection state */
  isSelected: boolean;
  /** Resolved tags for the document */
  tags: TagReference[];
  /** Show/hide Checkbox column */
  showCheckbox?: boolean;
  /** Show/hide Version column */
  showVersion?: boolean;
  /** Show/hide the date column (upload or file-modified date, per the header's switch) */
  showDateCreated?: boolean;
  /** Show/hide Status column */
  showStatus?: boolean;
  /** Show/hide the Uploader column */
  showUser?: boolean;
  /** The Uploader column was asked for but did not fit: name them in the date's tooltip. */
  uploaderInDateHint?: boolean;
  /** The list is sorted by size: show each file's size beside its name. */
  sizeInName?: boolean;
  /** Show/hide Tags column */
  showTags?: boolean;
  /** Show/hide Parent column */
  showParent?: boolean;
  /** Show/hide Actions column */
  showActions?: boolean;
  /** Callback when selection changes */
  onSelect: (isSelected: boolean) => void;
  /** Capture-phase click handler for range/modifier selection. Runs before the
   *  inner checkbox's onClick so shift/ctrl clicks can stopPropagation and prevent
   *  Radix from toggling the row. */
  onRowClickCapture?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Callback when download is clicked */
  onDownload: () => void;
  /** Callback when preview is clicked (for PDFs and DWG files) */
  onPreview?: (activatingControl?: HTMLElement) => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
  /** Callback when parent document download is clicked */
  onDownloadParent?: (parentId: string) => void;
  /** Callback when container file is clicked to show children */
  onShowChildren?: () => void;
  /** Callback when version badge is clicked to show version history */
  onShowVersionHistory?: () => void;
  /** Callback when "Add new version" is clicked in the row actions menu. */
  onAddNewVersion?: () => void;
  /** True when this row IS an older version rendered inside an inline
   *  expansion under its latest-version anchor row. Used to indent the row. */
  isExpandedVersion?: boolean;
  /** When true, an expanded (older) version row shows a real selection checkbox
   *  instead of the empty placeholder — so two versions of the same file can be
   *  multi-selected (e.g. to compare IFCs). Ignored on anchor rows, which are
   *  always selectable. */
  versionSelectable?: boolean;
  /** Whether the anchor row is currently expanded — drives the version
   *  badge's chevron indicator. Only meaningful on anchor rows. */
  isVersionExpanded?: boolean;
  /** Show download action (default: true) */
  showDownloadAction?: boolean;
  /** Show delete action (default: true) */
  showDeleteAction?: boolean;
  /** Custom delete button label (default: "Delete", or "Restore" when isRestore is true) */
  deleteLabel?: string;
  /** Render the destructive action as a non-destructive Restore action. Used in trash view. */
  isRestore?: boolean;
  /** Resolved folder path for tooltip breadcrumb (e.g. "Project / Folder / Subfolder") */
  folderPath?: string;
  /** Structured breadcrumb segments for interactive tooltip navigation */
  breadcrumbSegments?: FolderPathSegment[];
  /** Building ID for breadcrumb navigation links */
  buildingId?: string;
  /** Additional CSS classes */
  className?: string;
  /** Callback when download preview PDF is clicked (for DWG files) */
  /** Callback when "View drawing" is clicked (for parent DWG files). */
  onOpenDrawingViewer?: () => void;
  /** Callback when Teach Franklin button is clicked to correct document tags */
  onTeachFranklin?: () => void;
  /** Callback when a tag X button is clicked to remove a tag */
  onTagRemove?: (tag: TagReference) => void;
  /** Whether this document was found via content search (not filename match) */
  isContentMatch?: boolean;
  /** First-match page/sheet + total match count for the Content chip tooltip.
   *  When omitted (or null) the tooltip falls back to the legacy "Found in
   *  document text" string. */
  contentMatchInfo?: ContentMatchInfo | null;
  /** Callback when Rename is clicked in the actions menu. When undefined the
   *  Rename action is hidden — used for permission gating by the parent. */
  onRename?: () => void;
  /** Toggle this row's Verified mark. Undefined hides the menu item. */
  onToggleVerified?: () => void;
  /** Open the comments panel on this row. Undefined hides the item and badge click. */
  onComments?: () => void;
  /** The hosting page's own action on this row's menu (the Brief page's "Add appendix"). */
  hostAction?: { label: string; icon: LucideIcon; onClick: () => void };
  /** Open / completed thread counts for the row badge. */
  commentCounts?: DocumentCommentCounts;
  /** Callback when Move/Copy is clicked in the actions menu. When provided the
   *  3-dots menu shows the same Move/Copy action that the bulk-action toolbar
   *  exposes — keeps the two surfaces consistent (WHA-3253). */
  onMoveCopy?: () => void;
  /** When true, render an inline-edit input over the filename instead of the
   *  static text. Other row metadata stays visible. */
  isRenaming?: boolean;
  /** Editable basename (without extension) — only used when isRenaming. */
  renameBasename?: string;
  /** File extension (with leading dot) — preserved during rename. */
  renameExtension?: string;
  /** Called as the user types during rename. */
  onRenameChange?: (value: string) => void;
  /** Called when rename is confirmed (Enter or blur). */
  onRenameConfirm?: () => void;
  /** Called when rename is cancelled (Escape). */
  onRenameCancel?: () => void;
  /** WHA-3270 — PM issues this document is attached to. Rendered as a small
   *  chip next to the preview icon. Empty array = no chip. */
  linkedIssues?: LinkedIssueRef[];
  /** Native move support, enabled only on the project Folders surface. */
  isDraggable?: boolean;
  isDragging?: boolean;
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  onDragEnd?: React.DragEventHandler<HTMLDivElement>;
}

/**
 * DocumentTableRow - Organism component
 *
 * Single table row with all document metadata.
 * Composes molecules and atoms into a complete row layout.
 * Handles row selection styling and hover states.
 */
export function DocumentTableRow({
  document,
  parentDocumentName,
  isSelected,
  tags,
  showCheckbox = true,
  showVersion = true,
  showDateCreated = true,
  showStatus = true,
  showUser = true,
  uploaderInDateHint = false,
  sizeInName = false,
  showTags = true,
  showParent = false,
  showActions = true,
  onSelect,
  onRowClickCapture,
  onDownload,
  onPreview,
  onDelete,
  onDownloadParent,
  onShowChildren,
  onShowVersionHistory,
  onAddNewVersion,
  isExpandedVersion = false,
  versionSelectable = false,
  isVersionExpanded = false,
  showDownloadAction = true,
  showDeleteAction = true,
  deleteLabel,
  isRestore = false,
  folderPath,
  breadcrumbSegments,
  buildingId,
  className,
  onOpenDrawingViewer,
  onTeachFranklin,
  onTagRemove,
  isContentMatch = false,
  contentMatchInfo,
  onRename,
  onToggleVerified,
  onComments,
  hostAction,
  commentCounts,
  isRenaming = false,
  renameBasename = "",
  renameExtension = "",
  onRenameChange,
  onRenameConfirm,
  onRenameCancel,
  onMoveCopy,
  linkedIssues,
  isDraggable = false,
  isDragging = false,
  onDragStart,
  onDragEnd,
}: DocumentTableRowProps) {
  const { t, language } = useLanguage();
  const signaturePanel = useSignaturePanel();
  // Check if this is a DWG file (for dual download options)
  const isDwgFile = document.filename?.toLowerCase().endsWith(".dwg");
  // Franklin's appendices PDF reads in the viewer's language; others as stored.
  const displayFilename = useFranklinDisplayName(
    buildingId,
    document.id,
    document.filename,
    language
  );
  const parentDocumentId = document.parent_document_id;
  // A host page's note on this row (the Brief: what confirming would record).
  const decorations = useDocumentRowDecorations();
  const hostChip = decorations?.documentChip?.(String(document.id)) ?? null;
  const hostHighlighted = decorations?.isHighlighted?.("document", String(document.id)) ?? false;
  // Every row is one line: the filename and the tags each truncate rather
  // than wrap, so the list keeps one row height whatever a row holds. The
  // notes the Tags cell shows in place of tags hold to one line too.
  const tagCellNoteClass = "min-w-0 truncate text-sm italic text-muted-foreground";
  // Shared with the header and the folder/container rows, so columns line up.
  const gridColumns = documentGridTemplate({
    showCheckbox,
    showDateCreated,
    showUser,
    showVersion,
    showStatus,
    showTags,
    showParent,
    showActions,
  });

  // Signed container context: this row IS an .asice, or it's a document sealed
  // inside one. Drives the seal, the chips and the signature panel. The parent
  // filename (already loaded from the store for the Parent column) lets the
  // hook skip the container lookup for DWG/IFC child rows.
  const signed = useSignedContainerInfo({ ...document, parentFilename: parentDocumentName });
  const openSignaturePanel =
    signed && signaturePanel
      ? () =>
          signaturePanel.open(
            signed.role === "container"
              ? { containerId: signed.info.containerId }
              : {
                  containerId: signed.info.containerId,
                  enclosedDocumentId: document.id,
                  enclosedFilename: document.filename,
                }
          )
      : undefined;

  // Computed: is this a clickable container that can show children? A signed
  // container is excluded — its title opens the signature panel instead. The
  // exclusion is derived from the filename (synchronous), not from the async
  // `signed` info, so an .asice row is never briefly misclassified as a
  // navigable container while the signature fetch is still in flight.
  const isClickableContainer =
    isContainerFile(document.filename) &&
    !!onShowChildren &&
    !isSignedContainerFile(document.filename);

  // Native DWG quick view is independent of the legacy child-PDF state gate:
  // DocumentListTable only supplies onPreview when it can open Drawings.
  const isNativeDrawingQuickView = document.filename.toLowerCase().endsWith(".dwg") && !!onPreview;

  // Computed: is the filename clickable to open a preview? Containers take precedence
  // (their title click opens the children dialog).
  const canPreviewFromTitle =
    !isClickableContainer &&
    (isNativeDrawingQuickView || canPreviewDocumentRow(document)) &&
    !!onPreview;

  // Nothing else claims the title, so a signed row's title opens the panel.
  const titleOpensSignaturePanel =
    !!openSignaturePanel && !isClickableContainer && !canPreviewFromTitle;

  // Handler for showing children - stops propagation and calls callback
  const handleShowChildren = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShowChildren?.();
  };

  // Handler for opening preview from filename click
  const handlePreviewClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onPreview?.(e.currentTarget);
  };

  // Filename trigger: container → opens children dialog; previewable doc → opens
  // preview; otherwise plain text. `data-row-title` tells the parent row-click
  // handler not to toggle selection when this element is clicked.
  const filenameTrigger = isClickableContainer ? (
    <button
      type="button"
      data-row-title
      className="min-w-0 cursor-pointer select-text truncate text-left text-sm font-medium hover:text-primary hover:underline"
      onClick={handleShowChildren}
    >
      {displayFilename}
    </button>
  ) : canPreviewFromTitle ? (
    <button
      type="button"
      data-row-title
      className="min-w-0 cursor-pointer truncate text-left text-sm font-medium hover:text-primary hover:underline"
      onClick={handlePreviewClick}
    >
      {displayFilename}
    </button>
  ) : titleOpensSignaturePanel ? (
    <button
      type="button"
      data-row-title
      className="min-w-0 cursor-pointer truncate text-left text-sm font-medium hover:text-primary hover:underline"
      onClick={(e) => {
        e.stopPropagation();
        openSignaturePanel?.();
      }}
    >
      {displayFilename}
    </button>
  ) : (
    <div
      data-row-title
      className={cn(
        "min-w-0 truncate text-sm font-medium",
        // A draggable row makes its title the drag handle, the way a file name
        // is in every file manager, and the browser gives that ancestor the
        // press before any selection starts. Advertising an I-beam there would
        // promise a text selection the drag has taken away.
        !isDraggable && "cursor-text select-text"
      )}
    >
      {displayFilename}
    </div>
  );

  return (
    <div
      data-document-id={document.id}
      className={cn(
        "group/row grid select-none items-center gap-4 border-b px-6 py-1.5 transition-colors",
        isSelected ? "border-primary/50 bg-primary/5 hover:bg-primary/10" : "hover:bg-[#f7f7f7]",
        isExpandedVersion && "bg-muted/10 pl-10",
        isDraggable && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-45",
        hostHighlighted && HIGHLIGHTED_ROW_CLASS,
        className
      )}
      data-highlighted={hostHighlighted || undefined}
      style={{
        gridTemplateColumns: gridColumns,
      }}
      onClickCapture={onRowClickCapture}
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {/* Checkbox — older-version rows are selectable only when `versionSelectable`
          (e.g. IFC versions the user can compare); otherwise they render an empty
          placeholder so the grid column stays aligned. */}
      {showCheckbox &&
        (isExpandedVersion && !versionSelectable ? (
          <div aria-hidden="true" />
        ) : (
          <DocumentCheckbox isSelected={isSelected} onSelect={onSelect} />
        ))}

      {/* Icon + filename and its marks, on one line. Nothing sits underneath
          any more: the upload date and uploader have their own columns, and
          the file size shows only while the list is sorted by size. */}
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex flex-shrink-0">
          {isClickableContainer ? (
            <button
              type="button"
              className="cursor-pointer hover:opacity-70"
              onClick={handleShowChildren}
            >
              <FileTypeIcon filename={document.filename} />
            </button>
          ) : signed ? (
            <SignedFileIcon filename={document.filename} status={signed.info.status} />
          ) : (
            <FileTypeIcon filename={document.filename} />
          )}
        </div>
        <div className="min-w-0 flex-1 @container">
          <div className="flex min-w-0 items-center gap-2">
            {isRenaming ? (
              <DocumentNameInlineEdit
                basename={renameBasename}
                extension={renameExtension}
                onChange={(value) => onRenameChange?.(value)}
                onConfirm={() => onRenameConfirm?.()}
                onCancel={() => onRenameCancel?.()}
                ariaLabel={`Rename ${document.filename}`}
              />
            ) : breadcrumbSegments && buildingId ? (
              <HoverCard openDelay={300} closeDelay={200}>
                <HoverCardTrigger asChild>{filenameTrigger}</HoverCardTrigger>
                <HoverCardContent
                  side="bottom"
                  align="start"
                  className="w-fit max-w-lg break-words p-2 text-sm"
                >
                  <BreadcrumbTooltipContent segments={breadcrumbSegments} buildingId={buildingId} />
                </HoverCardContent>
              </HoverCard>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>{filenameTrigger}</TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-lg break-words">
                    {folderPath ? (
                      <span className="flex flex-wrap items-center gap-1">
                        {folderPath.split(" / ").map((segment, i, arr) => {
                          const isFirst = i === 0;
                          const isLast = i === arr.length - 1;
                          return (
                            <span key={i} className="flex items-center gap-1">
                              {i > 0 && (
                                <ChevronRight className="h-3 w-3 flex-shrink-0 text-muted-foreground/60" />
                              )}
                              <span
                                className={cn(
                                  isFirst && "font-semibold",
                                  isLast && "text-foreground",
                                  !isFirst && !isLast && "text-muted-foreground"
                                )}
                              >
                                {segment}
                              </span>
                            </span>
                          );
                        })}
                      </span>
                    ) : (
                      document.filename
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Franklin's logo right after the name, then the host's chip. */}
            <FranklinMadeMark buildingId={buildingId} documentId={document.id} />
            {hostChip && <RowChipBadge chip={hostChip} />}
            {/* Signed container: the container format the document is sealed
                in, or the enclosed-documents peek. The signature STATUS is the
                seal on the file icon alone — no written status in the lists. */}
            {signed &&
              (signed.role === "enclosed" ? (
                <InContainerChip containerFilename={signed.info.containerFilename} />
              ) : (
                <EnclosedDocumentsMenu
                  containerId={signed.info.containerId}
                  containerFilename={signed.info.containerFilename}
                />
              ))}
            {/* Content match indicator */}
            {isContentMatch && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                      <FileText className="h-3 w-3" />
                      <span className="hidden @[500px]:inline">
                        {t.documents.table.contentMatch}
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {formatContentMatchTooltip(contentMatchInfo, t.documents.table)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Sorted by size (Storage → Free space lands here): the order
                needs the value it is ordered by. */}
            {sizeInName && typeof document.size === "number" && (
              <span
                className="flex-shrink-0 text-xs tabular-nums text-muted-foreground"
                data-testid="document-row-size"
              >
                {formatFileSize(document.size)}
              </span>
            )}
            {/* Quick view: native viewer for DWGs, inline preview otherwise. */}
            {(isNativeDrawingQuickView || canPreviewDocumentRow(document)) && onPreview && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 flex-shrink-0 p-0 text-muted-foreground hover:bg-neutral-200 hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(e.currentTarget);
                      }}
                      aria-label="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="z-50">
                    Preview
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Verified mark — beside the filename, never inside DocumentTags
                (whose overflow would collapse it into "+N more"). */}
            <VerifiedBadge
              verifiedAt={document.verified_at}
              verifiedByName={document.verified_by_name}
              // Same squeeze rule as the content-match chip above: below the
              // name cell's @[500px] the label drops and only the icon stays.
              collapsible
            />
            <CommentCountBadge counts={commentCounts} onClick={onComments} />
            {/* WHA-3270: linked PM issues chip — sits beside the Eye icon. */}
            {linkedIssues && linkedIssues.length > 0 && <LinkedIssuesChip issues={linkedIssues} />}
            {/* Container files button - shows children */}
            {isClickableContainer && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 flex-shrink-0 p-0 text-muted-foreground hover:bg-neutral-200 hover:text-foreground"
                      onClick={handleShowChildren}
                      aria-label="Show contained files"
                    >
                      <FolderOpen className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="z-50">
                    Show contained files
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {/* Uploaded or File modified — the header's switch decides which */}
      {showDateCreated && (
        <DocumentDateCell
          document={document}
          uploaderName={uploaderInDateHint ? document.created_by_name : null}
        />
      )}

      {/* Uploader: initials, full name and picture on hover */}
      {showUser && (
        <DocumentUploaderCell
          createdBy={document.created_by}
          createdByName={document.created_by_name}
          createdByAvatarUrl={document.created_by_avatar_url}
          createdByColor={document.created_by_color}
        />
      )}

      {/* Version */}
      {showVersion && (
        <div className="flex items-center justify-center">
          <VersionBadge
            version={document.version ?? 1}
            hasHistory={
              !isExpandedVersion && (document.version ?? 1) > 1 && !!document.version_group
            }
            isExpanded={isVersionExpanded}
            onClick={isExpandedVersion ? undefined : onShowVersionHistory}
          />
        </div>
      )}

      {/* Status */}
      {showStatus && (
        <div className="flex items-center justify-center">
          <DocumentStateBadge state={document.state ?? undefined} />
        </div>
      )}

      {/* Tags */}
      {showTags && (
        <div className="flex min-w-0 items-center">
          {document.state?.toUpperCase() === "ERROR" ? (
            <span className={tagCellNoteClass}>
              {isSupportedFiletype(document.filename)
                ? t.documents.status.processingFailed
                : t.documents.status.unsupportedFiletype}
            </span>
          ) : document.state?.toUpperCase() === "UNZIPPED" ? (
            <span className={tagCellNoteClass}>{t.documents.status.zipFile}</span>
          ) : isDocumentProcessing(document.state) && (!tags || tags.length === 0) ? (
            <span className={tagCellNoteClass}>{t.documents.status.processing}</span>
          ) : (
            <>
              <DocumentTags
                tags={tags}
                showAll={false}
                maxVisible={4}
                onTagRemove={onTagRemove}
                onOverflowClick={onTeachFranklin}
                singleLine
                suffix={
                  onTeachFranklin ? (
                    <DocumentTagEditorButton empty={!tags?.length} onClick={onTeachFranklin} />
                  ) : undefined
                }
              />
              {isDocumentProcessing(document.state) && (
                <Loader2 className="ml-1.5 h-3.5 w-3.5 flex-shrink-0 animate-spin text-muted-foreground" />
              )}
            </>
          )}
        </div>
      )}

      {/* Parent Document */}
      {showParent && (
        <div className="min-w-0">
          {parentDocumentId && parentDocumentName && (
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 break-words text-xs text-muted-foreground">
                {parentDocumentName}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 flex-shrink-0 p-0"
                onClick={() => onDownloadParent?.(parentDocumentId)}
                aria-label="Download parent document"
              >
                <Download className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex items-center justify-end" data-actions>
          <DocumentActions
            onDownload={onDownload}
            onDelete={onDelete}
            showDownload={showDownloadAction}
            showDelete={showDeleteAction}
            deleteLabel={deleteLabel}
            isRestore={isRestore}
            isDwgFile={isDwgFile}
            onOpenDrawingViewer={onOpenDrawingViewer}
            onViewSignatures={
              !isRestore && signaturePanel && canInspectSignatures(document.filename)
                ? () =>
                    signaturePanel.open({
                      containerId: signed?.info.containerId ?? String(document.id),
                    })
                : undefined
            }
            onAddNewVersion={!isExpandedVersion ? onAddNewVersion : undefined}
            opensVersioning={hasVersionHistory(document)}
            onRename={!isExpandedVersion ? onRename : undefined}
            onToggleVerified={!isExpandedVersion ? onToggleVerified : undefined}
            isVerified={document.verified_at != null}
            onComments={!isExpandedVersion ? onComments : undefined}
            hostAction={!isExpandedVersion ? hostAction : undefined}
            onMoveCopy={!isExpandedVersion ? onMoveCopy : undefined}
            showProjectActions={!isExpandedVersion && !!onMoveCopy}
          />
        </div>
      )}
    </div>
  );
}
